<script lang="ts">
    import {
        VpArrowUpDownIcon,
        VpCloseIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createDialog, melt } from '@melt-ui/svelte';
    import { tick } from 'svelte';
    import { fade } from 'svelte/transition';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import { flyDrawer } from '$saga/utils/customTransitions';
    import { filtersContext, sortValue } from '$saga/views/Search/store';
    import { Sorting } from '$saga/views/Search/types';

    const sortingEntries = [
        { value: Sorting.PRICE_ASC, label: trans('search.sort.price_asc') },
        { value: Sorting.PRICE_DESC, label: trans('search.sort.price_desc') },
        {
            value: Sorting.DURATION_ASC,
            label: trans('search.sort.duration_asc'),
        },
        {
            value: Sorting.DURATION_DESC,
            label: trans('search.sort.duration_desc'),
        },
        { value: Sorting.REVIEWS_DESC, label: trans('search.sort.reviews') },
        {
            value: Sorting.DEPARTURE_DATE,
            label: trans('search.sort.departure_date'),
        },
    ];

    /**
     * Add specific "default" recommended sort based on if we are on a landing page or not
     */
    $: visibleSortingEntries = (() => {
        let recommendedSort = Sorting.DEFAULT;
        if ($filtersContext.isLandingPage) {
            switch ($filtersContext.landingPageType) {
                case 'sport':
                    recommendedSort = Sorting.SORT_SPORT_RANKING;
                    break;
                case 'destination':
                    recommendedSort =
                        Sorting.SORT_LANDING_DESTINATION_PERSONALIZED;
                    break;
                case 'theme':
                    recommendedSort = Sorting.SORT_LANDING_TAG_PERSONALIZED;
                    break;
                default:
                    recommendedSort = Sorting.SORT_LANDING_PERSONALIZED;
                    break;
            }
        }
        return [
            {
                value: recommendedSort,
                label: trans('search.sort.recommendation'),
            },
            ...sortingEntries,
        ];
    })();

    const {
        elements: { trigger, title, overlay, content, close, portalled },
        states: { open },
    } = createDialog({
        forceVisible: true,
    });

    const updateSort = async (event: Event) => {
        const target = event.currentTarget as HTMLSelectElement;
        $sortValue = target.value;

        // update UI so that hidden input receive new value
        await tick();
        const form = document.getElementById(
            'travel-search-engine-form',
        ) as HTMLFormElement | null;
        form?.requestSubmit();

        $open = false;
    };
</script>

<button
    class={cn(
        'relative top-0 flex h-8 w-full transform items-center justify-center gap-2xs rounded-full px-m py-xs text-xs',
        'border border-solid border-border-neutral bg-white hover:border-border-brand hover:text-content-brand',
    )}
    use:melt={$trigger}
>
    <span class="flex items-center gap-2xs">
        <VpArrowUpDownIcon class="size-l" />
        <span>{trans('search.sort.title')}</span>
    </span>
</button>

{#if $open}
    <div use:melt={$portalled}>
        <div
            use:melt={$overlay}
            class="fixed inset-0 z-40 bg-black/50"
            transition:fade={{ duration: 250 }}
        ></div>
        <div
            use:melt={$content}
            class="fixed inset-x-0 bottom-0 z-50 flex h-[60vh] max-h-[calc(100%)] w-full flex-col bg-white transition-all duration-500 desktop:inset-x-[unset] desktop:right-0 desktop:top-0 desktop:h-full desktop:min-w-[33vw] desktop:max-w-[33vw]"
            transition:flyDrawer
        >
            <div class="flex items-center p-m">
                <h2 class="mx-auto font-semibold" {...$title} use:title>
                    {trans('search.sort.title')}
                </h2>
                <div>
                    <button
                        {...$close}
                        use:close
                        aria-label={trans('action.close')}
                    >
                        <VpCloseIcon class="size-xl" />
                    </button>
                </div>
            </div>
            <section class="flex h-full flex-col overflow-hidden">
                {#each visibleSortingEntries as sortingEntry (sortingEntry.value)}
                    <label
                        for={sortingEntry.value}
                        class="flex cursor-pointer items-center gap-m border-b border-solid border-border-quiet px-l py-s"
                    >
                        <input
                            id={sortingEntry.value}
                            type="radio"
                            name="sort"
                            value={sortingEntry.value}
                            checked={sortingEntry.value === $sortValue}
                            on:change={updateSort}
                        />
                        {sortingEntry.label}
                    </label>
                {/each}
            </section>
        </div>
    </div>
{/if}
